import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { getRolesMeAPI } from '../components/Role/roleAPI';

const RolesPermissionsContext = createContext(null);

export const ROLES_ME_STORAGE_KEY = 'roles_me_from_login';

function readStoredLoginRoles() {
  try {
    const raw = sessionStorage.getItem(ROLES_ME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const d = parsed?.data ?? parsed;
    return {
      is_super_admin: d?.is_super_admin === true || d?.is_super_admin === 1 || String(d?.is_super_admin).toLowerCase() === 'true',
      permissions: Array.isArray(d?.modules) && d.modules.length
        ? d.modules
        : (Array.isArray(d?.permission_keys) ? d.permission_keys : []),
      provider_ids: Array.isArray(d?.provider_ids) ? d.provider_ids : [],
      return_providers: Array.isArray(d?.return_providers) ? d.return_providers : [],
      roles: Array.isArray(d?.roles) ? d.roles : [],
    };
  } catch {
    return null;
  }
}

/**
 * Default permission key prefixes per menu/route (for subadmin menu visibility).
 * Menu item is shown if user has at least one permission key that starts with the prefix.
 */
export const MENU_PERMISSION_PREFIXES = {
  '/category': 'Category',
  '/service': 'Service',
  '/department': 'Department',
  '/customers': 'Customers',
  '/tickets': 'Ticket',
  '/ticket-report': 'Ticket',
  '/complain': 'Issue Categories',
  '/provider': 'Provider',
  '/role': 'Roles',
  '/assignRole': 'Assign Role',
  '/activityLogs': 'Activity Logs',
  '/contactUs': 'Contact Us',
  '/order-returns': 'Order Return',
};

export function RolesPermissionsProvider({ children }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [providerIds, setProviderIds] = useState([]);
  const [returnProviders, setReturnProviders] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fetchingRef = React.useRef(false);

  const applyRolesData = useCallback((input) => {
    if (!input) return;

    const d = input?.data ?? input;
    // Safety check for is_super_admin
    const superAdmin = d.is_super_admin === true || d.is_super_admin === 1 || String(d.is_super_admin).toLowerCase() === 'true';
    setIsSuperAdmin(superAdmin);

    // Backend change: permissions are strictly under 'modules' now
    const permissionsList = Array.isArray(d?.modules) && d.modules.length
      ? d.modules
      : (Array.isArray(d?.permission_keys) ? d.permission_keys : []);

    setRoles(Array.isArray(d?.roles) ? d.roles : []);
    setPermissions(permissionsList);
    setProviderIds(Array.isArray(d?.provider_ids) ? d.provider_ids : []);
    setReturnProviders(Array.isArray(d?.return_providers) ? d.return_providers : []);
    setLoaded(true);
  }, []);

  const fetchRolesMe = useCallback(async () => {
    if (loaded || fetchingRef.current) return;
    fetchingRef.current = true;

    const fromLogin = readStoredLoginRoles();
    if (fromLogin) {
      sessionStorage.removeItem(ROLES_ME_STORAGE_KEY);
      applyRolesData(fromLogin);
      fetchingRef.current = false;
      return;
    }

    try {
      const res = await getRolesMeAPI();
      const body = res?.data ?? {};
      if (body.status === true || body.status === 'true') {
        const d = body.data ?? body;
        applyRolesData(d);
      } else {
        setRoles([]);
        setPermissions([]);
        setProviderIds([]);
        setReturnProviders([]);
        setIsSuperAdmin(false);
      }
    } catch (_err) {
      setRoles([]);
      setPermissions([]);
      setProviderIds([]);
      setReturnProviders([]);
      setIsSuperAdmin(false);
    } finally {
      setLoaded(true);
      fetchingRef.current = false;
    }
  }, [loaded, applyRolesData]);

  const refreshRolesMe = useCallback(async () => {
    if (fetchingRef.current) return { success: false, error: 'Already fetching' };
    fetchingRef.current = true;
    try {
      const res = await getRolesMeAPI();
      const body = res?.data ?? {};
      if (body.status === true || body.status === 'true') {
        const d = body.data ?? body;
        applyRolesData(d);
        return { success: true };
      }
      return { success: false, error: body.message || 'Permissions fetch failed' };
    } catch (err) {
      return { success: false, error: err?.response?.data?.message || err?.message || 'Network error' };
    } finally {
      fetchingRef.current = false;
    }
  }, [applyRolesData]);

  const hasPermission = useCallback(
    (key) => {
      if (!key) return true;
      if (!loaded) return true;
      if (isSuperAdmin) return true;

      const lowerKey = String(key).toLowerCase().replace(/\s+/g, '');

      const checkArray = (arr) => {
        if (!Array.isArray(arr)) return false;
        return arr.some((k) => {
          if (!k) return false;
          const val = typeof k === 'string' ? k : (k.key || k.name || k.module || String(k));
          const lowerVal = String(val).toLowerCase().replace(/\s+/g, '');
          // Match exactly OR match as a prefix (e.g. "category" matches "category.create")
          return lowerVal === lowerKey || lowerKey.startsWith(lowerVal + '.');
        });
      };

      return checkArray(permissions);
    },
    [loaded, permissions, isSuperAdmin]
  );

  const hasAnyPermission = useCallback(
    (prefix) => {
      if (!prefix) return true;
      if (!loaded) return true;
      if (isSuperAdmin) return true;

      const lowerPrefix = String(prefix).toLowerCase().replace(/\s+/g, '');

      const checkArray = (arr) => {
        if (!Array.isArray(arr)) return false;
        return arr.some((k) => {
          if (!k) return false;
          const val = typeof k === 'string' ? k : (k.key || k.name || k.module || String(k));
          const lowerVal = String(val).toLowerCase().replace(/\s+/g, '');
          return lowerVal.startsWith(lowerPrefix) || lowerPrefix.startsWith(lowerVal + '.');
        });
      };

      return checkArray(permissions);
    },
    [loaded, permissions, isSuperAdmin]
  );

  const showMenuForPath = useCallback(
    (path) => {
      if (isSuperAdmin) return true;
      const prefix = MENU_PERMISSION_PREFIXES[path];
      if (!prefix) return true;
      const lowerPrefix = prefix.toLowerCase();

      const checkArray = (arr) => {
        if (!Array.isArray(arr)) return false;
        return arr.some((k) => {
          if (!k) return false;
          const val = typeof k === 'string' ? k : (k.key || k.name || k.module || String(k));
          const lowerVal = String(val).toLowerCase();
          return lowerVal === lowerPrefix || lowerPrefix.startsWith(lowerVal + '.');
        });
      };

      return checkArray(permissions);
    },
    [isSuperAdmin, permissions]
  );

  const value = useMemo(
    () => ({
      roles,
      permissions,
      providerIds,
      returnProviders,
      isSuperAdmin,
      loaded,
      fetchRolesMe,
      refreshRolesMe,
      hasPermission,
      hasAnyPermission,
      showMenuForPath,
      getProviderIds: () => providerIds,
      getReturnProviders: () => returnProviders,
    }),
    [
      roles,
      permissions,
      providerIds,
      returnProviders,
      isSuperAdmin,
      loaded,
      fetchRolesMe,
      refreshRolesMe,
      hasPermission,
      hasAnyPermission,
      showMenuForPath,
      returnProviders,
    ]
  );

  return (
    <RolesPermissionsContext.Provider value={value}>
      {children}
    </RolesPermissionsContext.Provider>
  );
}

export function useRolesPermissions() {
  const ctx = useContext(RolesPermissionsContext);
  if (!ctx) {
    return {
      roles: [],
      permissions: [],
      providerIds: [],
      returnProviders: [],
      isSuperAdmin: false,
      loaded: true,
      fetchRolesMe: async () => { },
      refreshRolesMe: async () => { },
      hasPermission: () => true,
      hasAnyPermission: () => true,
      showMenuForPath: () => true,
      getProviderIds: () => [],
      getReturnProviders: () => [],
    };
  }
  return ctx;
}
